
import React from 'react';
import { Check, X, HelpCircle, Zap, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TooltipProvider, Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Card, CardContent } from '../ui/card';
import CTAButton from '../CTAButton';
import { PlanData, PlanFeature, PlanName } from './types';

interface PlanComparisonTableProps {
  plans: PlanData[];
  featureCategories: {
    name: string;
    features: PlanFeature[];
  }[];
}

const PlanComparisonTable = ({
  plans,
  featureCategories
}: PlanComparisonTableProps) => {
  const renderFeatureValue = (plan: PlanData, value: string | number | boolean) => {
    if (typeof value === 'boolean') {
      return value ? <div className="flex justify-center">
          <Check className="h-5 w-5 text-green-500" />
        </div> : <div className="flex justify-center">
          <X className="h-5 w-5 text-red-500" />
        </div>;
    }
    return <div className="text-center font-medium">{value}</div>;
  };
  
  // Define the payment URL based on the plan
  const getPaymentUrl = (planName: PlanName) => {
    if (planName === "Starter") {
      return "https://buy.stripe.com/7sI17E39Pfsu3Zu7sG";
    } else if (planName === "Pro") {
      return "https://buy.stripe.com/cN2bMi8u9fsu0NiaET";
    } else if (planName === "Unlimited") {
      return "https://buy.stripe.com/8wM9Ea9yd6VYcw03ci";
    }
    return undefined; // Return undefined for other plans to use the default behavior
  };
  
  return <>
      {/* Plan headers */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <div className="col-span-1 flex items-end">
          <div className="w-full">
            <div className="mb-6">
              <h3 className="text-[#003a70] text-3xl font-bold flex items-center gap-2 mb-2">
                <span>Compare Plans</span>
              </h3>
              <p className="text-gray-600 text-lg">Compare plans and choose what's right for you.</p>
            </div>
            
            <Card className="border-[#FEE6D8] bg-gradient-to-br from-white to-[#FEE6D8]/30 overflow-hidden shadow-md">
              <CardContent className="p-5">
                <div className="flex gap-4 items-start">
                  <div className="bg-[#003a70]/10 rounded-full p-2 flex-shrink-0">
                    <Info className="h-5 w-5 text-[#003a70]" />
                  </div>
                  <div>
                    <p className="text-[#003a70] font-semibold text-lg mb-3">Every plan includes:</p>
                    <ul className="text-sm text-gray-700 space-y-3">
                      <li className="flex items-center gap-2.5">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <span>AI Assistants, Smart Forms & Flow Builder</span>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <span>Bring Your Own API Key (BYOK)</span>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <span>Webhook Integrations</span>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <span>Chat Logs & Lead Capture</span>
                      </li>
                      <li className="flex items-center gap-2.5">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check className="h-4 w-4 text-green-600" />
                        </div>
                        <span>Upgrade or Cancel Anytime</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {plans.map(plan => <div key={plan.name} className={cn("col-span-1 rounded-xl px-6 pt-6 pb-6 text-center transition-all duration-300", plan.highlight ? "bg-[#003a70] text-white shadow-xl relative transform hover:-translate-y-1" : plan.color + " hover:shadow-md")}>
            <div className="absolute -top-3 inset-x-0 flex justify-center">
              {plan.highlight && <Badge variant="secondary" className="bg-[#FEE6D8] text-[#003a70] px-3 py-1 shadow">
                  Recommended
                </Badge>}
            </div>
            
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className={cn("rounded-full p-2", plan.highlight ? "bg-white/20" : "bg-[#003a70]/10")}>
                {plan.icon}
              </div>
              <Badge variant={plan.highlight ? "secondary" : "outline"} className={cn(plan.highlight ? "bg-white/20 text-white border-0" : "bg-[#003a70]/10 text-[#003a70] border-0")}>
                {plan.tagline}
              </Badge>
            </div>
            
            <h3 className="text-xl font-bold mb-3">{plan.name}</h3>
            
            <div className="flex items-baseline justify-center gap-1 mb-4">
              <span className={cn("text-4xl font-bold", plan.highlight ? "text-white" : "text-[#003a70]")}>
                {plan.price}
              </span>
              <span className={plan.highlight ? "text-white/80" : "text-gray-600"}>
                {plan.period}
              </span>
            </div>
            
            <p className={cn("text-sm mb-6 h-12", plan.highlight ? "text-white/90" : "text-gray-600")}>
              {plan.description}
            </p>
            
            <CTAButton 
              variant={plan.highlight ? "primary" : "accent"} 
              className={cn(
                "w-full transform transition-transform hover:scale-105",
                // Override the large CTAButton sizing for table context
                "!py-3 !px-4 !text-sm !min-h-[44px] !rounded-lg",
                plan.highlight ? "bg-white text-[#003a70] hover:bg-white/90" : ""
              )}
              to={getPaymentUrl(plan.name)}
            >
              {plan.ctaText}
            </CTAButton>
          </div>)}
      </div>
      
      {/* Feature comparison table */}
      <div className="border rounded-xl overflow-hidden bg-white shadow mb-8">
        {featureCategories.map((category, categoryIndex) => <div key={category.name} className={categoryIndex % 2 === 0 ? "" : "bg-gray-50"}>
            {/* Category Header */}
            <div className={cn("px-6 py-4 border-b", categoryIndex % 2 === 0 ? "bg-gray-50" : "bg-white")}>
              <h4 className="font-bold text-[#003a70]">{category.name}</h4>
            </div>
            
            {/* Features */}
            {category.features.map((feature, featureIndex) => <div key={feature.name} className={cn("grid grid-cols-4 gap-6 px-6 py-4 hover:bg-[#FEE6D8]/10 transition-colors", featureIndex !== category.features.length - 1 ? "border-b" : "", categoryIndex !== featureCategories.length - 1 && featureIndex === category.features.length - 1 ? "border-b-2" : "")}>
                <div className="col-span-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">{feature.name}</span>
                    {feature.description && <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-xs bg-[#003a70] text-white border-0 p-3">
                            <p>{feature.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>}
                  </div>
                </div>
                
                {plans.map(plan => <div key={`${feature.name}-${plan.name}`} className={cn("col-span-1 text-center py-1", plan.highlight ? "bg-[#FEE6D8]/10 rounded-lg" : "")}>
                    {renderFeatureValue(plan, feature.values[plan.name])}
                  </div>)}
              </div>)}
          </div>)}
      </div>
      
      {/* Bottom CTA buttons */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <div className="col-span-1"></div>
        {plans.map(plan => <div key={`bottom-cta-${plan.name}`} className="col-span-1">
            <CTAButton 
              variant={plan.highlight ? "primary" : "accent"} 
              className={cn(
                "w-full transform transition-transform hover:scale-105",
                // Override the large CTAButton sizing for table context
                "!py-3 !px-4 !text-sm !min-h-[44px] !rounded-lg",
                plan.highlight ? "bg-[#003a70] text-white hover:bg-[#003a70]/90" : ""
              )}
              to={getPaymentUrl(plan.name)}
            >
              {plan.ctaText}
            </CTAButton>
          </div>)}
      </div>
    </>;
};

export default PlanComparisonTable;
